import axios from 'axios'
import {
  ASTREA_HOMOLOG_OVERRIDE_STORAGE_KEY,
  ASTREA_HOMOLOG_OVERRIDE_TARGETS,
  ASTREA_PLAYGROUND_URL_STORAGE_KEY,
} from '../constants/astreaHomologOverride'

/**
 * Central HTTP client (axios) — replaces the legacy AngularJS
 * `$http` + `app/interceptors/httpInterceptor.js` pair.
 *
 * Legacy behavior being ported (confirmed by reading the AngularJS source):
 * - `app/interceptors/httpInterceptor.js` pushed a `$httpProvider` request
 *   interceptor that set `config.headers['Authorization'] =
 *   'c2VydmxldEJhc2ljQXV0aDp2ZXJzYW8yMA=='` on every single outgoing
 *   request, unconditionally.
 * - Several services (`bankSlipService.js`, `actionAuditService.js`,
 *   `devService.js`, `tenantService.js`, `elasticService.js`,
 *   `historiesService.js`) additionally set the same header explicitly via
 *   `AuthService.getServletBasicAuth().replace('Basic ', '')` — i.e. the
 *   *value* sent is always the bare Base64 token, never the
 *   RFC-7617-style `Basic <token>` string, even though
 *   `authService.getServletBasicAuth()` itself returns the `Basic `-prefixed
 *   form. This looks like a long-standing quirk/bug in the legacy app (the
 *   backend servlet apparently expects the bare token), but changing it is
 *   out of scope here — task 008 only ports existing behavior 1:1. Kept
 *   exactly as-is below.
 */

/**
 * Fixed "servlet Basic Auth" token. This is not a per-user secret — it is
 * a single shared credential already shipped in the AngularJS bundle today
 * (visible in `app/interceptors/httpInterceptor.js`), so moving it out of
 * source control does not change its actual exposure (anyone can already
 * read it from the current production JS bundle). Per the task's request to
 * avoid a bare literal in the new code without a documented decision: the
 * value is read from `VITE_SERVLET_BASIC_AUTH` (see `client/.env.example`)
 * so it can be rotated per-environment without a code change, and falls
 * back to the current known-public value so local dev keeps working
 * out of the box without requiring every contributor to set up a `.env`
 * file. If this token is ever rotated to protect something sensitive, it
 * must also be re-evaluated on the backend/servlet side, and the fallback
 * below should be removed at that point.
 */
const SERVLET_BASIC_AUTH_FALLBACK = 'c2VydmxldEJhc2ljQXV0aDp2ZXJzYW8yMA=='

const SERVLET_BASIC_AUTH = import.meta.env.VITE_SERVLET_BASIC_AUTH || SERVLET_BASIC_AUTH_FALLBACK

const ASTREA_HOMOLOG_OVERRIDE_HEADER = 'X-Astrea-Homolog-Override'
const ASTREA_PLAYGROUND_URL_HEADER = 'X-Astrea-Playground-Url'

/** Name of the session cookie set by `authService.login()` (`$cookies.put('sessionId', ...)`). */
export const SESSION_COOKIE_NAME = 'sessionId'

/** Path the app should redirect to on 401/403, equivalent to the legacy `login` state. */
export const LOGIN_PATH = '/login'

/**
 * Reads a cookie by name from `document.cookie`. No third-party cookie
 * library is introduced for this alone — the app only ever needs to *read*
 * one cookie here as a fallback (see `sessionTokenProvider` below);
 * writing/removing session state is owned by `AuthContext`
 * (`client/src/shared/context/AuthContext.jsx`, task 009).
 */
function readCookie(name) {
  if (typeof document === 'undefined') return undefined

  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : undefined
}

/**
 * Session token provider, set by `AuthContext` on login/logout
 * (`setSessionTokenProvider`) instead of `httpClient` reaching into
 * `AuthContext`'s storage directly — keeps this module framework-agnostic
 * and free of a dependency on React/context. Defaults to reading the
 * legacy `sessionId` cookie so `httpClient` keeps working even if
 * `AuthContext` hasn't registered a provider yet (e.g. very first paint,
 * or in tests that exercise `httpClient` in isolation, like
 * `httpClient.test.js`).
 */
let sessionTokenProvider = () => readCookie(SESSION_COOKIE_NAME)

/**
 * Registers the function `httpClient` calls to obtain the current session
 * token for outgoing requests. `AuthContext` calls this once on mount with
 * a function that returns its in-memory Firebase ID token (falling back to
 * the legacy `sessionId` cookie for the pre-Firebase/legacy-login path —
 * see `AuthContext.jsx`), so every request always carries the freshest
 * token without `httpClient` importing React context directly.
 */
export function setSessionTokenProvider(provider) {
  sessionTokenProvider = provider || (() => readCookie(SESSION_COOKIE_NAME))
}

export const httpClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
})

// Request interceptor: always attach the fixed servlet Basic Auth header,
// equivalent to the legacy global `HttpInterceptor.request`.
httpClient.interceptors.request.use((config) => {
  config.headers = config.headers || {}
  config.headers['Authorization'] = SERVLET_BASIC_AUTH

  // Second interceptor concern: forward the user's session token.
  // `withCredentials: true` above already makes the browser send the
  // legacy `sessionId` cookie automatically as a cookie on same-site/CORS-
  // with-credentials requests, so no extra header is strictly required for
  // that flow to keep working as-is. It is also mirrored into an
  // `X-Session-Id` header for endpoints/environments where the cookie may
  // not be forwarded (e.g. cross-origin dev proxies), and — since task 009
  // (Firebase auth) — for the Firebase ID token itself, which has no
  // cookie equivalent and MUST be sent explicitly. `AuthContext` registers
  // the actual token source via `setSessionTokenProvider`; see its
  // doc-comment above.
  //
  // Only attached for requests to our own `server_app` (relative URLs) —
  // NOT for the many hooks that call the external Astrea API directly with
  // an absolute URL (`${getUrlAstreaPath()}/api/v2/...`, e.g.
  // `useCustomerQuery.js`'s `getContracts`). That API's CORS preflight only
  // ever allowed `Authorization` (the one header the legacy `$http` calls
  // to it also sent); adding an extra custom header it never allow-lists
  // makes the browser's preflight fail with a CORS error, even though the
  // request would otherwise succeed. `server_app` itself has no such
  // restriction since it's same-origin (or same-origin-via-Vite-proxy in
  // dev) and is the one place this header is actually consumed.
  const isSameOriginRequest = !/^https?:\/\//i.test(config.url || '')

  // The external Astrea API doesn't need cookies (auth is the header
  // above) and its CORS response doesn't echo the request origin, which
  // `withCredentials: true` requires — sending it cross-origin makes the
  // browser reject the response.
  config.withCredentials = isSameOriginRequest

  const sessionToken = sessionTokenProvider()
  if (sessionToken && isSameOriginRequest) {
    config.headers['X-Session-Id'] = sessionToken
  }

  const homologOverrideTarget = localStorage.getItem(ASTREA_HOMOLOG_OVERRIDE_STORAGE_KEY)
  if (isSameOriginRequest && ASTREA_HOMOLOG_OVERRIDE_TARGETS.includes(homologOverrideTarget)) {
    config.headers[ASTREA_HOMOLOG_OVERRIDE_HEADER] = homologOverrideTarget

    if (homologOverrideTarget === 'PLAYGROUND') {
      const playgroundUrl = localStorage.getItem(ASTREA_PLAYGROUND_URL_STORAGE_KEY)
      if (playgroundUrl) {
        config.headers[ASTREA_PLAYGROUND_URL_HEADER] = playgroundUrl
      }
    }
  }

  return config
})

// Response interceptor: centralized error handling. Equivalent to the
// legacy `requireLogin` / `$stateChangeStart` guard in `app/main.js`, which
// kept unauthenticated users out of protected states — here we additionally
// react to the backend actively rejecting a request (401/403) at any time
// (e.g. an expired session on an already-open screen) by sending the user
// back to the login screen.
httpClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response ? error.response.status : undefined

    if (status === 401 || status === 403) {
      if (typeof window !== 'undefined' && window.location.pathname !== LOGIN_PATH) {
        window.location.assign(LOGIN_PATH)
      }
    }

    return Promise.reject(error)
  },
)

export default httpClient
